import { Component, inject, signal, computed, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { SeriesService, SeriesFormatDto } from '../../services/series';
import { DropdownService, DropdownItem } from '../../services/dropdown';
import { ToastService } from '../../services/toast';
import { PageTitleService } from '../../services/page-title';

@Component({
  selector: 'app-series-format',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    DialogModule,
    SelectModule,
    ButtonModule,
    TableModule,
    TooltipModule,
    InputTextModule,
  ],
  templateUrl: './series-format.html',
  styleUrls: ['./series-format.css'],
})
export class SeriesFormat implements OnInit {
  private fb = inject(FormBuilder);
  private svc = inject(SeriesService);
  private dropdown = inject(DropdownService);
  private toast = inject(ToastService);
  private platformId = inject(PLATFORM_ID);
  /** Page heading — pulled from the menu so it always matches the clicked item. */
  readonly pageTitle = inject(PageTitleService).title;

  readonly tokenTypes = ['Prefix', 'Text', 'Number', 'Year', 'Month', 'Day', 'Separator'];

  loading = signal(false);
  saving = signal(false);
  editing = signal(false);
  showModal = signal(false);
  formats = signal<any[]>([]);
  masters = signal<DropdownItem[]>([]);
  selectedSeries = signal<number | null>(null);

  form: FormGroup = this.fb.group({
    id: [0],
    seriesId: [null, Validators.required],
    tokenType: ['Prefix', Validators.required],
    tokenValue: [''],
    tokenOrder: [1, Validators.required],
    padding: [null as number | null],
  });

  // Optionally filter the list by the selected series.
  visibleFormats = computed(() => {
    const sel = this.selectedSeries();
    const all = this.formats();
    if (sel == null) return all;
    return all.filter((f) => f.seriesId === sel);
  });

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.load();
      this.loadMasters();
    }
  }

  load() {
    this.loading.set(true);
    this.svc.getAllFormats().subscribe({
      next: (res: any) => {
        this.loading.set(false);
        const data = res?.data ?? res ?? [];
        this.formats.set(Array.isArray(data) ? data : []);
      },
      error: () => this.loading.set(false),
    });
  }

  loadMasters() {
    this.dropdown.seriesMaster().subscribe((opts) => this.masters.set(opts));
  }

  seriesName(id: number) {
    const m = this.masters().find((x) => +x.value === id);
    return m ? m.label : `#${id}`;
  }

  openAdd() {
    this.editing.set(false);
    const nextOrder = this.visibleFormats().length + 1;
    this.form.reset({
      id: 0,
      seriesId: this.selectedSeries(),
      tokenType: 'Prefix',
      tokenValue: '',
      tokenOrder: nextOrder,
      padding: null,
    });
    this.showModal.set(true);
  }

  openEdit(f: any) {
    this.editing.set(true);
    this.form.reset({
      id: f.id,
      seriesId: f.seriesId,
      tokenType: f.tokenType ?? 'Prefix',
      tokenValue: f.tokenValue ?? '',
      tokenOrder: f.tokenOrder ?? 1,
      padding: f.padding ?? null,
    });
    this.showModal.set(true);
  }

  closeModal() {
    this.showModal.set(false);
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toast.warning('Series, token type and order are required');
      return;
    }
    this.saving.set(true);
    const v = this.form.value;
    const dto: SeriesFormatDto = {
      id: v.id,
      seriesId: Number(v.seriesId),
      tokenType: v.tokenType,
      tokenValue: v.tokenValue || null,
      tokenOrder: Number(v.tokenOrder),
      padding: v.padding != null && v.padding !== '' ? Number(v.padding) : null,
    };
    const req = this.editing() ? this.svc.updateFormat(dto) : this.svc.insertFormat(dto);

    req.subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to save format');
          return;
        }
        this.toast.success(this.editing() ? 'Format updated' : 'Format token added');
        this.closeModal();
        this.load();
      },
      error: () => this.saving.set(false),
    });
  }

  async remove(f: any) {
    const ok = await this.toast.confirm('This format token will be deleted.', 'Delete token?', 'Delete');
    if (!ok) return;
    this.svc.deleteFormat(f.id).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to delete');
          return;
        }
        this.toast.success('Format token deleted');
        this.load();
      },
    });
  }

  // Build a live preview of the series pattern from its tokens.
  preview = computed(() => {
    const tokens = [...this.visibleFormats()].sort(
      (a, b) => (a.tokenOrder ?? 0) - (b.tokenOrder ?? 0),
    );
    if (!tokens.length) return '';
    return tokens
      .map((t) => {
        switch ((t.tokenType || '').toLowerCase()) {
          case 'number': {
            const pad = t.padding ?? 4;
            return '1'.padStart(pad, '0');
          }
          case 'year':
            return 'YYYY';
          case 'month':
            return 'MM';
          case 'day':
            return 'DD';
          default:
            return t.tokenValue ?? '';
        }
      })
      .join('');
  });
}
